import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getSalesByMitra, getAllSales } from '../../supabase/database';
import { FaMoneyBillWave, FaPiggyBank, FaBook, FaPaperPlane, FaUserTie } from 'react-icons/fa';
import Spinner from '../../components/ui/Spinner';

// Memoized StatCard untuk performance
const StatCard = memo(({ icon, title, value, color, loading }) => {
  const colorClass = useMemo(() => {
    const colors = {
      green: 'from-green-500 to-green-400',
      purple: 'from-purple-500 to-purple-400',
      blue: 'from-blue-500 to-blue-400',
      orange: 'from-orange-500 to-orange-400',
    };
    return colors[color] || colors.blue;
  }, [color]);

  return (
    <div className={`bg-gradient-to-br ${colorClass} text-white p-6 rounded-xl shadow-lg flex items-center justify-between`}>
      <div>
        <p className="text-lg font-semibold">{title}</p>
        <p className="text-4xl font-bold">
          {loading ? '...' : value}
        </p>
      </div>
      <div className="text-5xl opacity-50">
        {icon}
      </div>
    </div>
  );
});

const MitraDashboard = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalOmset: 0,
    totalKomisi: 0,
    validatedCount: 0,
    pendingCount: 0,
  });
  const [loading, setLoading] = useState(true);

  // Memoized fetchStats untuk performance
  const fetchStats = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    try {
      setLoading(true);
      
      // Test basic sales query
      const testSales = await getSalesByMitra(currentUser.uid);
      
      // Also try with currentUser.id if uid doesn't work
      if ((!testSales || testSales.length === 0) && currentUser.id && currentUser.id !== currentUser.uid) {
        const testSales2 = await getSalesByMitra(currentUser.id);
      }
      
      // Update logika: gunakan status 'dikirim seluruhnya' dan 'diproses' sebagai validated
      const [processedSales, deliveredSales, pendingSales] = await Promise.all([
        getSalesByMitra(currentUser.uid, 'diproses'),
        getSalesByMitra(currentUser.uid, 'dikirim seluruhnya'),
        getSalesByMitra(currentUser.uid, 'menunggu validasi')
      ]);
      
      // Gabungkan sales yang diproses dan dikirim sebagai validatedSales
      const validatedSales = [...(processedSales || []), ...(deliveredSales || [])];
      
      // Filter hanya penjualan yang sudah lunas untuk perhitungan komisi
      const fullyPaidSales = (validatedSales || []).filter(sale => sale.pembayaran === 'lunas');
      
      const totalOmset = (validatedSales || []).reduce((acc, sale) => acc + (Number(sale.subtotal) || 0), 0);
      // Komisi hanya dihitung dari penjualan yang sudah lunas
      const totalKomisi = fullyPaidSales.reduce((acc, sale) => acc + ((Number(sale.hargaSetelahDiskon) || Number(sale.subtotal) || 0) * 0.15), 0);

      setStats({
        totalOmset,
        totalKomisi,
        validatedCount: (validatedSales || []).length,
        pendingCount: (pendingSales || []).length,
      });
    } catch (error) {
      setStats({
        totalOmset: 0,
        totalKomisi: 0,
        validatedCount: 0,
        pendingCount: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="bg-yellow-500 text-white py-2 px-4 mb-6 rounded-md overflow-hidden">
        <marquee behavior="scroll" direction="left">
          Selamat datang di sistem kemitraan NH Press
        </marquee>
      </div>

      <div className="flex justify-between items-center mb-8">
        <div>
            <h1 className="text-3xl font-bold text-gray-800">Dashboard Mitra</h1>
            <p className="text-gray-600 mt-1">Selamat datang, {currentUser?.email}!</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-5xl text-purple-500 opacity-75">
              <FaUserTie />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard 
          icon={<FaMoneyBillWave />} 
          title="Total Omset Anda" 
          value={`Rp ${stats.totalOmset.toLocaleString('id-ID')}`} 
          color="green" 
          loading={loading}
        />
        <StatCard 
          icon={<FaPiggyBank />} 
          title="Komisi (Pembayaran Lunas)" 
          value={`Rp ${stats.totalKomisi.toLocaleString('id-ID')}`} 
          color="purple" 
          loading={loading}
        />
        <StatCard 
          icon={<FaBook />} 
          title="Penjualan Diproses/Dikirim" 
          value={stats.validatedCount} 
          color="blue" 
          loading={loading}
        />
        <StatCard 
          icon={<FaPaperPlane />} 
          title="Menunggu Validasi" 
          value={stats.pendingCount} 
          color="orange" 
          loading={loading}
        />
      </div>

      <div className="mt-8 bg-gray-50 p-6 rounded-xl">
        <h2 className="text-xl font-bold mb-4 text-gray-700">Riwayat Penjualan Terakhir</h2>
        {loading ? <Spinner /> : <p className="text-gray-500">Komponen untuk daftar penjualan terakhir akan ditampilkan di sini...</p>}
      </div>
    </div>
  );
};

export default MitraDashboard;